import React, { useState, useEffect, useCallback, useMemo } from "react";

import type { CreatedClientSummary } from "@/modules/dashboard/clients/components/create/ClientSuccessStep";
import { ClientsService } from "@/modules/dashboard/clients/services/clientsService";
import type { ClientAccount } from "@/modules/dashboard/clients/types/clients-api.types";
import {
  clientDisplayName,
  normalizeClientListItem,
} from "@/modules/dashboard/clients/utils/normalizeClient";
import type { Translations } from "@/shared/lang/interfaces/translationTypes";
import useTranslations from "@/shared/lang/useTranslations";

import { getCountryFieldConfig } from "../../config/countryFields.config";
import { useDispersions } from "../../hooks/useDispersions";
import type { CountryCode } from "../../types/countryFields.types";
import type { SelectFieldConfig } from "../../types/formField.types";
import { ClientKycUnavailableAlert } from "./ClientKycUnavailableAlert";
import { FieldInput, FieldSelect } from "./fields";
import { RegisterClientAction } from "./RegisterClientAction";

const PIX_TYPE_LABEL_TO_NUMBER: Record<string, number> = {
  CPF: 0,
  CNPJ: 1,
  Email: 2,
  EMAIL: 2,
  Teléfono: 3,
  "Llave aleatoria": 4,
  EVP: 4,
};

interface ClientEntry {
  id: string;
  displayName: string;
}

interface ClientOption {
  label: string;
  value: string;
  /** "Not verified" KYC tag rendered right-aligned in the option row. Search matches `label` only. */
  badge?: string;
}

interface AccountOption {
  label: string;
  value: string;
}

interface RecipientClientSelectorProps {
  destination: CountryCode;
  destinationCurrency: string;
}

export const RecipientClientSelector: React.FC<RecipientClientSelectorProps> = ({
  destination,
  destinationCurrency,
}) => {
  const { formData, updateRecipient, updateField, verifiedClientIds, fetchVerifiedClientIds } =
    useDispersions();
  const { t } = useTranslations("dispersions");

  const [clients, setClients] = useState<ClientEntry[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [accounts, setAccounts] = useState<ClientAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  // Clients registered through the recipient-step shortcut: not KYC-verified yet — flagged locally so the
  // badge shows even before/without the org-detail data.
  const [injectedClientIds, setInjectedClientIds] = useState<ReadonlySet<string>>(new Set());
  // Bumped after the shortcut creates an account so the effect refetches the (already selected) client.
  const [accountsRefreshKey, setAccountsRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Server-side KYC FILTER disabled (business decision): the select lists EVERY registered client so
    // operators see the whole portfolio — unverified clients get a "Not verified" badge, a blocking note
    // when selected, and the step-2 gate (DispersionsListPage) prevents continuing until they verify.
    // Two facts back removing the param:
    //   1) The dispersal payload (`POST /dispersions/{code}`) carries no client reference and the backend
    //      enforces no KYC on it — the gating is a frontend business rule (now enforced at step 2).
    //   2) The `kyc_status: "VERIFIED"` param this fetch used to send was actually INERT: the backend list
    //      (`findAll` in coloca-nest `clients.internal.service.ts`) declares `kyc_status` in its query DTO
    //      but never applies it to the SQL, so the param filtered nothing.
    // The per-client status comes from the shared clientKyc slice (this list response carries no KYC data).
    ClientsService.fetchList({ skip: 0, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        setClients(
          res.data.data.map((raw) => {
            const c = normalizeClientListItem(raw);
            return {
              id: c.id,
              displayName: clientDisplayName(c),
            };
          }),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setClients([]);
      })
      .finally(() => {
        if (cancelled) return;
        setClientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The paginated list carries NO KYC data (no `identity.kyc_provider` join on the backend); the real
  // per-client status lives in the dispersions slice (fetched once per session from the organization
  // detail) so this panel, the runtime panels and the step-2 gate all share the same source.
  useEffect(() => {
    fetchVerifiedClientIds();
  }, [fetchVerifiedClientIds]);

  const verifiedSet = useMemo(
    () => (verifiedClientIds === null ? null : new Set(verifiedClientIds)),
    [verifiedClientIds],
  );

  useEffect(() => {
    const clientId = formData.selectedClientId;
    if (!clientId) return;
    let cancelled = false;
    // Loading flag must fire before the fetch; benign single setState (no cascade).
    // oxlint-disable-next-line react-hooks-eslint/set-state-in-effect
    setAccountsLoading(true);
    ClientsService.fetchAccounts(clientId)
      .then((res) => {
        if (cancelled) return;
        setAccounts(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setAccounts([]);
      })
      .finally(() => {
        if (cancelled) return;
        setAccountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.selectedClientId, accountsRefreshKey]);

  /** Marks the option's "Not verified" badge and the dispersal block. Unknown (null) never flags — fail open. */
  const isClientUnverified = useCallback(
    (id: string) => injectedClientIds.has(id) || (verifiedSet !== null && !verifiedSet.has(id)),
    [injectedClientIds, verifiedSet],
  );

  const clientOptions: ClientOption[] = useMemo(
    () =>
      clients.map((c) => ({
        label: c.displayName,
        value: c.id,
        badge: isClientUnverified(c.id) ? t("client_not_verified_badge") : undefined,
      })),
    [clients, isClientUnverified, t],
  );

  const filteredAccounts = useMemo(
    () => accounts.filter((a) => a.currency === destinationCurrency),
    [accounts, destinationCurrency],
  );

  const accountOptions: AccountOption[] = useMemo(
    () =>
      filteredAccounts.map((a) => ({
        label: `${a.bank_name ?? t("recipient_account_fallback_name")} - ${a.currency ?? ""} (${a.payment_method ?? ""})`,
        value: a.id,
      })),
    [filteredAccounts, t],
  );

  const clearRecipientFields = useCallback(() => {
    const fieldConfigs = getCountryFieldConfig(destination);
    for (const f of fieldConfigs) {
      updateRecipient(f.name, "");
    }
  }, [destination, updateRecipient]);

  const handleClientChange = useCallback(
    (value: string) => {
      updateField("selectedClientId", value || null);
      updateField("selectedClientAccountId", null);
      updateField("selectedClientAccountMethod", null);
      clearRecipientFields();
    },
    [updateField, clearRecipientFields],
  );

  // Shared by the manual account selection and the register-client shortcut (which holds the account
  // object before the refetched list contains it).
  const applyAccount = useCallback(
    (account: ClientAccount, beneficiaryName?: string) => {
      updateField("selectedClientAccountId", account.id);
      clearRecipientFields();
      updateField("selectedClientAccountMethod", account.bank_name?.trim().toUpperCase() ?? null);
      if (beneficiaryName) {
        updateRecipient("beneficiary", beneficiaryName);
      }

      const SKIP_FIELDS = new Set(["beneficiary", "description"]);
      const fieldConfigs = getCountryFieldConfig(destination);
      const requiredFields = fieldConfigs.filter((f) => f.required && !SKIP_FIELDS.has(f.name));
      const textRequiredFields = requiredFields.filter((f) => f.type === "text" || f.type === "email");
      // Legacy accounts only stored `account_key`; map it back to the single
      // text/email key field (Colombia key_number, Mexico clabe, Brasil pix_key).
      // Multi-text-field countries (Chile, Peru, Panamá, etc.) need full metadata.
      const fallbackTextFieldName = textRequiredFields.length === 1 ? textRequiredFields[0].name : null;

      for (const f of requiredFields) {
        const metaValue = account.account_metadata?.[f.name];
        if (metaValue !== undefined && metaValue !== null && metaValue !== "") {
          const isNumericSelect = f.type === "select" && f.options.every((o) => typeof o.value === "number");
          updateRecipient(f.name, isNumericSelect ? Number(metaValue) : metaValue);
        } else if (f.name === fallbackTextFieldName && account.account_key) {
          updateRecipient(f.name, account.account_key);
        }
      }

      if (destination === "Brasil") {
        const pixTypeField = requiredFields.find((f) => f.name === "pix_type");
        const currentPixType = account.account_metadata?.["pix_type"];
        if (
          pixTypeField &&
          (currentPixType === undefined || currentPixType === null || currentPixType === "")
        ) {
          const legacy = account.account_metadata?.["tipoLlavePix"];
          if (legacy !== undefined && legacy !== null) {
            const num = PIX_TYPE_LABEL_TO_NUMBER[String(legacy)];
            if (num !== undefined) updateRecipient("pix_type", num);
          }
        }
      }
    },
    [destination, updateField, updateRecipient, clearRecipientFields],
  );

  const handleAccountChange = useCallback(
    (value: string) => {
      const accountId = value || null;
      const account = accountId ? filteredAccounts.find((a) => a.id === accountId) : undefined;
      if (!account) {
        updateField("selectedClientAccountId", accountId);
        clearRecipientFields();
        updateField("selectedClientAccountMethod", null);
        return;
      }
      applyAccount(account, clients.find((c) => c.id === formData.selectedClientId)?.displayName);
    },
    [filteredAccounts, clients, formData.selectedClientId, applyAccount, updateField, clearRecipientFields],
  );

  const handleRegisteredClient = useCallback(
    (client: CreatedClientSummary) => {
      setClients((prev) =>
        prev.some((c) => c.id === client.id)
          ? prev
          : [{ id: client.id, displayName: client.displayName }, ...prev],
      );
      setInjectedClientIds((prev) => {
        if (prev.has(client.id)) return prev;
        const next = new Set(prev);
        next.add(client.id);
        return next;
      });
      // Same guard as handleClientChange: a repeated call must not wipe the account selection.
      if (formData.selectedClientId !== client.id) {
        updateField("selectedClientId", client.id);
        updateField("selectedClientAccountId", null);
        updateField("selectedClientAccountMethod", null);
        clearRecipientFields();
      }
    },
    [formData.selectedClientId, updateField, clearRecipientFields],
  );

  const handleRegisteredAccount = useCallback(
    (client: CreatedClientSummary, account: ClientAccount) => {
      handleRegisteredClient(client);
      applyAccount(account, client.displayName);
      setAccountsRefreshKey((k) => k + 1);
    },
    [handleRegisteredClient, applyAccount],
  );

  const isMexico = destination === "México";

  const brazilPixTypeOptions = useMemo(() => {
    const field = getCountryFieldConfig("Brasil").find((f) => f.name === "pix_type") as
      | SelectFieldConfig
      | undefined;
    return (field?.options ?? []).map((o: { label: string; value: unknown }) => ({
      label: o.label,
      value: String(o.value),
    }));
  }, []);

  const pixTypeValue = formData.recipientFields.pix_type;
  const needsPixTypeInput =
    destination === "Brasil" &&
    !!formData.selectedClientAccountId &&
    (pixTypeValue === "" || pixTypeValue === undefined || pixTypeValue === null);

  const selectedPixTypeOption =
    !needsPixTypeInput &&
    destination === "Brasil" &&
    pixTypeValue !== "" &&
    pixTypeValue !== undefined &&
    pixTypeValue !== null
      ? (brazilPixTypeOptions.find((o) => o.value === String(pixTypeValue)) ?? null)
      : null;

  const recipientSummaryFields = useMemo(() => {
    if (!formData.selectedClientAccountId) return [];
    const SKIP = new Set(["description"]);
    return getCountryFieldConfig(destination).filter(
      (f) =>
        !SKIP.has(f.name) &&
        formData.recipientFields[f.name] !== "" &&
        formData.recipientFields[f.name] !== undefined &&
        formData.recipientFields[f.name] !== null,
    );
  }, [formData.selectedClientAccountId, formData.recipientFields, destination]);

  return (
    <div className="space-y-4">
      {/* KYC verification unavailable → the step-2 gate is failing closed; offer the retry. */}
      <ClientKycUnavailableAlert />

      <div className="space-y-2">
        <FieldSelect
          id="selectedClient"
          label={t("recipient_client_label")}
          labelAction={
            <RegisterClientAction
              destinationCurrency={destinationCurrency}
              onClientCreated={handleRegisteredClient}
              onAccountCreated={handleRegisteredAccount}
            />
          }
          placeholder={clientsLoading ? t("recipient_client_loading") : t("recipient_client_placeholder")}
          options={clientOptions}
          value={formData.selectedClientId ?? ""}
          onValueChange={handleClientChange}
          disabled={clientsLoading}
        />
        {!!formData.selectedClientId && isClientUnverified(formData.selectedClientId) && (
          <div
            aria-live="polite"
            className="rounded-lg border border-warning-container-foreground/20 bg-warning-container px-3.5 py-2 text-sm text-warning-container-foreground"
          >
            {t("client_kyc_blocked_note")}
          </div>
        )}
      </div>

      {formData.selectedClientId && (
        <div className="space-y-2">
          <FieldSelect
            id="selectedAccount"
            label={t("recipient_account_label")}
            placeholder={
              accountsLoading ? t("recipient_account_loading") : t("recipient_account_placeholder")
            }
            options={accountOptions}
            value={formData.selectedClientAccountId ?? ""}
            onValueChange={handleAccountChange}
            disabled={accountsLoading}
          />
          {!accountsLoading && filteredAccounts.length === 0 && accounts.length > 0 && (
            <p className="text-sm text-muted-foreground">
              No hay cuentas en {destinationCurrency} para este cliente
            </p>
          )}
        </div>
      )}

      {needsPixTypeInput && (
        <FieldSelect
          id="pix_type"
          label={t("recipient_pix_type_label")}
          placeholder={t("recipient_pix_type_placeholder")}
          options={brazilPixTypeOptions}
          value={selectedPixTypeOption?.value ?? ""}
          onValueChange={(value) => {
            if (value) updateRecipient("pix_type", Number(value));
          }}
        />
      )}

      {recipientSummaryFields.length > 0 && (
        <div className="space-y-2 rounded-lg border border-border bg-muted px-4 py-3">
          {recipientSummaryFields.map((f) => {
            const value = formData.recipientFields[f.name];
            const displayValue =
              f.type === "select"
                ? (f.options.find((o) => String(o.value) === String(value))?.label ?? String(value))
                : String(value);
            return (
              <div key={f.name} className="flex justify-between items-center gap-4">
                <span className="shrink-0 text-xs text-muted-foreground">
                  {t(f.label as keyof Translations["dispersions"]) as string}
                </span>
                <span className="text-right text-xs font-medium text-foreground">{displayValue}</span>
              </div>
            );
          })}
        </div>
      )}

      {formData.selectedClientAccountId && !isMexico && (
        <FieldInput
          id="description"
          label={t("recipient_description_label")}
          placeholder={t("recipient_description_placeholder")}
          value={(formData.recipientFields.description as string) ?? ""}
          onValueChange={(value) => updateRecipient("description", value)}
        />
      )}
    </div>
  );
};
